/**
 * ProductCard Component
 *
 * Product card with mockup preview using the 5-layer system.
 * Shows real mockup templates from the API instead of SVG fallbacks.
 */

import {
  useState,
  useRef,
  useLayoutEffect,
  useMemo,
  useCallback,
  useEffect,
  memo,
  type ChangeEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Switch } from '@/components/ui';
import { cn, formatPrice } from '@/lib/utils';
import { ChevronUp, Pencil } from 'lucide-react';
import {
  MockupThumbnail,
  default as MockupRenderer,
} from '@/components/editor/MockupRenderer';
import { useInView } from '@/hooks/useInView';
import { prefetchMockupLayers } from '@/hooks/useMockupLayers';
import { useDesignPricing } from '@/hooks/useDesigns';
import { VariantSurchargeList } from './VariantSurchargeList';
import { grossOf, netOf, round2 } from '@/lib/pricing-math';
import { useProductSelectionStore, useDesignStore, useUIStore } from '@/stores';
import { resolveEditPlacementDesignId } from './editPlacementTarget';
import { canEditPlacement } from './productCardActions';
import { useSwatchCapacity } from './swatchCapacity';
import { resolveDisplayColor } from './displayColor';
import {
  cardMethodLabel,
  isEmbroideryProduct,
  type DecorationCategory,
} from '@/lib/decoration-method';
import { useCanvasStateManager } from '@/stores/canvasStateManager';
import {
  findTemplateByPosition,
  getDefaultTemplate,
} from '@/lib/product-transformer';
import type {
  UIProduct,
  UIProductColor,
  UIProductView,
  UIProductMockupTemplate,
} from '@/lib/product-transformer';

// Re-export types for backwards compatibility
export type Product = UIProduct;
export type ProductColor = UIProductColor;
export type ProductView = UIProductView;

/**
 * Position preview data for front/back designs
 */
export interface PositionPreviewData {
  front?: string | null;
  back?: string | null;
  [key: string]: string | null | undefined;
}

interface ProductCardProps {
  product: UIProduct;
  /** Design preview for front position (data URL) */
  frontPreviewUrl?: string | null;
  /** Design preview for back position (data URL) */
  backPreviewUrl?: string | null;
  /**
   * Stufe 4.5: Per-position adaptation data for this product.
   * positionSlug → { placement, cropUrl }
   * Replaces the old single adaptedPlacement + designCropUrl props.
   */
  positionAdaptations?: Map<
    string,
    import('@/hooks/useAdaptedPreviews').PerPositionAdaptation
  > | null;
  mockupScale?: number;
  mockupContainerClassName?: string;
  isCompactColors?: boolean;
  isFocused?: boolean;
  focusedColorId?: string | null;
  onToggle: (productId: string, isActive: boolean) => void;
  onColorToggle: (productId: string, colorId: string) => void;
  onProductClick?: (productId: string, colorId: string | null) => void;
  onColorClick?: (productId: string, colorId: string) => void;
  /**
   * Why this product does not carry the design (step-2 filter). The card is
   * dimmed and the reason shown; the product stays selectable.
   */
  mismatchLabel?: string | null;
  /** Effective design width per Position on this product, e.g. "Front 23 cm". */
  areaChips?: string[];
  /** Images fall below the method's minimum dpi on this product. */
  dpiWarning?: string | null;
  /**
   * The run of the design shown on the card; an embroidery run names the
   * product's embroidery method instead of its catalogue default.
   */
  designDecoration?: DecorationCategory | null;
  /** Whether to show the "Only this product / All products" switch popover */
  showSwitchPopover?: boolean;
  /** Callback for "Only this product" popover action */
  onSingleProduct?: () => void;
  /** Callback for "All products" popover action */
  onAllProducts?: () => void;
  /** Callback to dismiss the popover */
  onDismissPopover?: () => void;
}

// ═══════════════════════════════════════════════════════════════════
// SVG FALLBACK MOCKUPS (when no API mockup templates available)
// ═══════════════════════════════════════════════════════════════════

/**
 * T-Shirt SVG for product mockup (fallback when no image)
 */
function TShirtMockup({
  color = '#FFFFFF',
  className,
}: {
  color?: string;
  className?: string;
}) {
  const isDark = color === '#000000' || color.toLowerCase() === '#1e3a5f';

  return (
    <div
      className={cn(
        'relative w-full h-full flex items-center justify-center',
        className
      )}
    >
      <svg
        className="drop-shadow-sm w-[85%] h-[85%]"
        viewBox="0 0 100 120"
        fill="none"
      >
        {/* T-Shirt shape */}
        <path
          d="M20 25 L5 40 L12 48 L20 42 L20 115 L80 115 L80 42 L88 48 L95 40 L80 25 L68 18 L62 25 L38 25 L32 18 L20 25Z"
          fill={color}
          stroke={isDark ? '#374151' : '#D1D5DB'}
          strokeWidth="1"
        />
        {/* Collar */}
        <ellipse
          cx="50"
          cy="22"
          rx="12"
          ry="6"
          fill={color}
          stroke={isDark ? '#374151' : '#D1D5DB'}
          strokeWidth="1"
        />
      </svg>
    </div>
  );
}

/**
 * Hoodie SVG for product mockup (fallback when no image)
 */
function HoodieMockup({
  color = '#FFFFFF',
  className,
}: {
  color?: string;
  className?: string;
}) {
  const isDark = color === '#000000' || color.toLowerCase() === '#1e3a5f';

  return (
    <div
      className={cn(
        'relative w-full h-full flex items-center justify-center',
        className
      )}
    >
      <svg
        className="drop-shadow-sm w-[85%] h-[85%]"
        viewBox="0 0 100 120"
        fill="none"
      >
        {/* Hoodie shape */}
        <path
          d="M15 30 L2 50 L12 56 L15 52 L15 115 L85 115 L85 52 L88 56 L98 50 L85 30 L70 20 L60 20 L50 12 L40 20 L30 20 L15 30Z"
          fill={color}
          stroke={isDark ? '#374151' : '#D1D5DB'}
          strokeWidth="1"
        />
        {/* Hood */}
        <path
          d="M40 20 L50 12 L60 20"
          fill="none"
          stroke={isDark ? '#374151' : '#D1D5DB'}
          strokeWidth="1"
        />
        {/* Pocket */}
        <path
          d="M30 85 L70 85 L70 100 L30 100 Z"
          fill="none"
          stroke={isDark ? '#374151' : '#D1D5DB'}
          strokeWidth="0.5"
        />
      </svg>
    </div>
  );
}

/**
 * Generic fallback mockup based on product name
 */
function FallbackMockup({
  product,
  color,
  className,
}: {
  product: UIProduct;
  color: string;
  className?: string;
}) {
  const name = product.name.toLowerCase();

  if (name.includes('hoodie') || name.includes('sweat')) {
    return <HoodieMockup color={color} className={className} />;
  }

  // Default: T-Shirt
  return <TShirtMockup color={color} className={className} />;
}

// ═══════════════════════════════════════════════════════════════════
// PRODUCT MOCKUP WITH 5-LAYER SYSTEM
// ═══════════════════════════════════════════════════════════════════

interface ProductMockupProps {
  product: UIProduct;
  template: UIProductMockupTemplate | null;
  backgroundColor: string;
  isHeathered?: boolean;
  size?: 'large' | 'small';
  scale?: number;
  className?: string;
  /** Design preview data URL to overlay on the mockup */
  designDataUrl?: string | null;
  /** Adapted design placement for this product (resolved for the specific position) */
  adaptedPlacement?: import('@designdeck/shared').AdaptedPlacement | null;
  /** Tight-crop image source for adapted mode (DataURL or S3 URL) */
  designCropUrl?: string | null;
  /** Colour behind the product layers, forwarded to the renderer. */
  frameBackground?: string;
}

/**
 * Product mockup using MockupRenderer (5-layer system)
 * Falls back to SVG if no template available
 *
 * When designDataUrl is provided, renders the design overlay on the mockup.
 * Otherwise uses simplified rendering (no design layer).
 */
export function ProductMockupWithTemplate({
  product,
  template,
  backgroundColor,
  isHeathered = false,
  size: _size = 'large', // Kept for API compatibility; dimensions are now always measured
  scale = 1,
  className,
  designDataUrl,
  adaptedPlacement,
  designCropUrl,
  frameBackground,
}: ProductMockupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Layers are requested once the card is near the viewport: the grid holds
  // the whole catalogue, and every card fetching its mask up front is what
  // made the creator steps load for ten seconds.
  const inView = useInView(containerRef);
  // Embroidery keeps the garment shading below the design (thread must not
  // be darkened by folds); every print method puts it above.
  const isEmbroidery = isEmbroideryProduct(product);
  // Default to reasonable size - will be updated by ResizeObserver immediately.
  // Using 200x200 as a safe default that works for both large and small mockups.
  const [measured, setMeasured] = useState({ width: 200, height: 200 });

  // ─────────────────────────────────────────────────────────────────
  // CONTAINER MEASUREMENT (ResizeObserver)
  // ─────────────────────────────────────────────────────────────────
  // useLayoutEffect ensures measurement happens before first paint (no flicker).
  // Measures container for both size="large" and size="small" to ensure
  // design positioning is always accurate relative to the actual container.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Synchronous initial measurement before first paint
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setMeasured({ width: rect.width, height: rect.height });
    }

    // Ongoing measurement for viewport/container resize
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry?.contentRect ?? { width: 0, height: 0 };
      if (width > 0 && height > 0) {
        setMeasured({ width, height });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // If no template, use fallback SVG
  if (!template) {
    return (
      <FallbackMockup
        product={product}
        color={backgroundColor}
        className={className}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────
  // DYNAMIC CONTAINER DIMENSIONS
  // ─────────────────────────────────────────────────────────────────
  // Both size="large" and size="small" use contain-fit into measured container.
  // This ensures design positioning is always accurate relative to actual container.
  const mockupWidth = template.mockupWidthPx || 500;
  const mockupHeight = template.mockupHeightPx || 500;

  const scaleX = measured.width / mockupWidth;
  const scaleY = measured.height / mockupHeight;
  const fitScale = Math.min(scaleX, scaleY) * scale;

  const dimensions = {
    width: Math.round(mockupWidth * fitScale),
    height: Math.round(mockupHeight * fitScale),
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full h-full flex items-center justify-center',
        className
      )}
    >
      {!inView ? null : designDataUrl || (adaptedPlacement && designCropUrl) ? (
        // Full MockupRenderer with design overlay
        // priority="low" ensures Creator loads first
        <MockupRenderer
          template={template}
          backgroundColor={backgroundColor}
          designDataUrl={designDataUrl}
          adaptedPlacement={adaptedPlacement}
          designCropUrl={designCropUrl}
          isHeathered={isHeathered}
          isEmbroidery={isEmbroidery}
          frameBackground={frameBackground}
          width={dimensions.width}
          height={dimensions.height}
          simplified={false}
          lazyLoad={true}
          priority="low"
          assetSize="card"
        />
      ) : (
        // Simplified thumbnail (no design layer)
        // Uses lazyLoad and low priority by default
        <MockupThumbnail
          template={template}
          backgroundColor={backgroundColor}
          width={dimensions.width}
          height={dimensions.height}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PRODUCT CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════

/**
 * Product Card component with Multi-View support (JUNIQE style)
 * - Uses 5-layer MockupRenderer for realistic previews
 * - Primary mockup shows the active position (front/back)
 * - Secondary thumbnail shows the other position
 * - Colors: one row sized to the card width, then chevron to expand
 * - Toggle: Violet when active
 *
 * PERFORMANCE: Memoized to prevent unnecessary re-renders when activePosition changes.
 * Templates are rendered with stable references - only size/position changes.
 */
export const ProductCard = memo(function ProductCard({
  product,
  frontPreviewUrl,
  backPreviewUrl,
  positionAdaptations,
  mockupScale = 1,
  mockupContainerClassName,
  isCompactColors = false,
  isFocused = false,
  focusedColorId,
  onToggle,
  onColorToggle,
  onProductClick,
  onColorClick,
  mismatchLabel = null,
  areaChips,
  dpiWarning = null,
  designDecoration = null,
  showSwitchPopover = false,
  onSingleProduct,
  onAllProducts,
  onDismissPopover,
}: ProductCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const selection = useProductSelectionStore(
    (state) => state.selections[product.id]
  );
  const setSellingPrice = useProductSelectionStore(
    (state) => state.setSellingPrice
  );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const designId = useDesignStore((state) => state.designId);
  const saveDesign = useDesignStore((state) => state.saveDesign);
  const hasUnsavedChanges = useDesignStore((state) => state.hasUnsavedChanges);
  const placementsByProduct = useCanvasStateManager(
    (state) => state.placementsByProduct
  );
  const currentStep = useUIStore((state) => state.currentStep);
  const [priceInput, setPriceInput] = useState<string>('');

  // Read activePosition from store instead of prop (avoids prop drilling, more stable)
  const activePosition = useProductSelectionStore(
    (state) => state.activePosition
  );

  const sellingPrice = selection?.sellingPrice;

  // The quote of this design — seller cost, profit and variant surcharges
  // per product — comes from the API, computed from the stored state, and
  // refreshes after every save (designStore.adoptPricingAfterSave). Only the
  // live profit of a typed draft is derived on the client (pricing-math).
  const pricingQuery = useDesignPricing(designId);
  const quote = useMemo(
    () =>
      pricingQuery.data?.products.find(
        (entry) => entry.productId === product.id
      ) ?? null,
    [pricingQuery.data, product.id]
  );
  const sellerCost =
    quote?.sellerCost != null ? Number(quote.sellerCost) : null;
  // While the seller types, the profit follows the draft; otherwise the
  // stored price. The seller cost is net, the price gross, so the draft is
  // brought to net with the quote's VAT rate first — the same single step
  // the server takes (19 % when an older API sends no rate). A draft under
  // the gross minimum shows the floor, unclamped — the server refuses it on
  // save with the same rule (price >= seller cost × 1.19, in cents).
  const draft = priceInput.trim();
  const draftPrice = Number(draft);
  const effectivePrice =
    draft !== '' && Number.isFinite(draftPrice) ? draftPrice : sellingPrice;
  const vatRate = quote?.vatRate;
  const netEffectivePrice =
    typeof effectivePrice === 'number' ? netOf(effectivePrice, vatRate) : null;
  const profit =
    netEffectivePrice !== null && sellerCost !== null
      ? round2(netEffectivePrice - sellerCost)
      : null;
  // The gross price the seller has to type to clear the floor.
  const floorPrice = sellerCost !== null ? grossOf(sellerCost, vatRate) : null;
  const belowFloor =
    typeof effectivePrice === 'number' &&
    floorPrice !== null &&
    round2(effectivePrice) < floorPrice;
  // A product saved with the design has a quote entry; one activated just
  // now gets its entry with the next save, so the figures wait, like the
  // first load.
  const quotePending =
    !!designId &&
    !pricingQuery.isError &&
    (pricingQuery.isPending || quote === null);
  const quoteFailed = !!designId && pricingQuery.isError;
  const priceState =
    sellingPrice === undefined
      ? sellerCost === null
        ? 'noPriceNoCost'
        : 'noPrice'
      : sellerCost === null
        ? 'noCost'
        : 'ok';

  // True while the seller's cursor is in the price field. A price that
  // changes in the store meanwhile (the start price adopted after an
  // autosave) must not replace what they are typing; their blur decides.
  const priceFieldFocused = useRef(false);

  useEffect(() => {
    if (priceFieldFocused.current) return;
    if (typeof sellingPrice === 'number') {
      setPriceInput(sellingPrice.toFixed(2));
      return;
    }
    setPriceInput('');
  }, [sellingPrice]);

  // Handle click on product card (not toggle)
  const handleCardClick = () => {
    onProductClick?.(product.id, displayColorId);
  };

  // Which color the card shows: focused color on the focused card,
  // otherwise the most recently selected color, otherwise the product's
  // first color, otherwise white (see resolveDisplayColor).
  const resolvedColor = useMemo(
    () =>
      resolveDisplayColor({
        colors: product.colors,
        selectedColors: product.selectedColors,
        focusedColorId: focusedColorId ?? null,
        isFocused,
      }),
    [product.colors, product.selectedColors, focusedColorId, isFocused]
  );

  const displayColor = resolvedColor.hex;

  // The ID corresponding to the currently displayed color (passed to placement edit URL)
  const displayColorId = resolvedColor.id;

  // Check if the displayed color is heathered - always matches the
  // currently displayed color so texture and badge stay in sync.
  const isHeathered = useMemo(() => {
    return (
      product.colors.find((c) => c.id === resolvedColor.id)?.isHeathered ??
      false
    );
  }, [product.colors, resolvedColor.id]);

  // Get front and back mockup templates (STABLE references via useMemo)
  const frontTemplate = useMemo(() => {
    return (
      findTemplateByPosition(product.mockupTemplates, 'front') ||
      getDefaultTemplate(product.mockupTemplates)
    );
  }, [product.mockupTemplates]);

  const backTemplate = useMemo(() => {
    return (
      findTemplateByPosition(product.mockupTemplates, 'back') ||
      findTemplateByPosition(product.mockupTemplates, 'back-inside')
    );
  }, [product.mockupTemplates]);

  // Stufe 4.5: Resolve per-position adaptations with slug fallbacks
  // Mirrors the backTemplate slug fallback logic (back → back-inside)
  const frontAdaptation = useMemo(
    () => positionAdaptations?.get('front') ?? null,
    [positionAdaptations]
  );
  const backAdaptation = useMemo(
    () =>
      positionAdaptations?.get('back') ??
      positionAdaptations?.get('back-inside') ??
      null,
    [positionAdaptations]
  );

  // Determine which position is active (back or front)
  const isBackActive =
    activePosition === 'back' || activePosition === 'back-inside';

  // Get design previews for each position (stable primitive props)
  const frontDesignUrl = frontPreviewUrl ?? null;
  const backDesignUrl = backPreviewUrl ?? null;

  // Stufe 5+: Check if this product has persisted custom placements.
  const hasOverrides = useMemo(() => {
    const productPlacements = placementsByProduct[product.id];
    return Boolean(
      productPlacements && Object.keys(productPlacements).length > 0
    );
  }, [placementsByProduct, product.id]);

  // Stufe 5.3: Whether this product has design data (can be adjusted)
  // On design step, positionAdaptations is null (master-only mode), so also check design URLs
  const hasDesignData =
    (positionAdaptations != null && positionAdaptations.size > 0) ||
    (currentStep === 'design' &&
      (frontDesignUrl != null || backDesignUrl != null));

  // Stufe 5.3: Whether the placement pencil is shown in the header (products step only)
  const showEditPlacement = canEditPlacement({
    isActive: product.isActive,
    hasDesignData,
    currentStep,
  });

  // Navigate to single-product edit mode (full canvas editor for this product)
  const handleEditPlacement = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const id = await resolveEditPlacementDesignId({
        designId,
        hasUnsavedChanges,
        saveDesign,
      });
      if (!id) {
        toast.error(t('creator.toast.saveFailed'));
        return;
      }
      const colorParam = displayColorId ? `&color=${displayColorId}` : '';
      navigate(`/creator/${id}?editProduct=${product.id}${colorParam}`);
    },
    [
      designId,
      hasUnsavedChanges,
      saveDesign,
      navigate,
      product.id,
      displayColorId,
      t,
    ]
  );

  // Prefetch mockup layers on hover for faster loading
  const handleMouseEnter = useCallback(() => {
    if (frontTemplate) {
      prefetchMockupLayers(frontTemplate);
    }
    if (backTemplate) {
      prefetchMockupLayers(backTemplate);
    }
  }, [frontTemplate, backTemplate]);

  // Collapsed swatch row: fill the card width with as many swatches as fit on
  // one line; the chevron expands to the full wrapped grid. The row is measured
  // (ResizeObserver) because the capacity depends on the card width, the label
  // and whether the chevron has to be reserved.
  const SWATCH_SIZE = 15; // px — matches the mockup's color dots exactly
  const ROW_GAP = 8; // px — gap-2 between label, swatches and chevron
  const FALLBACK_COLLAPSED_COLORS = 3; // used until the row has been measured
  const [colorRowEl, setColorRowEl] = useState<HTMLDivElement | null>(null);
  const colorCount = product.colors.length;
  const collapsedCapacity =
    useSwatchCapacity(
      colorRowEl,
      colorCount,
      {
        swatchSize: SWATCH_SIZE,
        gap: 6, // px — gap-1.5
        // The chevron, the gap before it and its ml-1.
        toggleWidth: (isCompactColors ? 24 : 20) + ROW_GAP + 4,
      },
      // The "Farben" label sits in front of the swatches.
      (row) =>
        row.clientWidth -
        ((row.firstElementChild as HTMLElement | null)?.offsetWidth ?? 0) -
        ROW_GAP
    ) ?? FALLBACK_COLLAPSED_COLORS;

  const hasMoreColors = colorCount > collapsedCapacity;
  const visibleColors = isExpanded
    ? product.colors
    : product.colors.slice(0, collapsedCapacity);

  // Same formatter as the catalogue card, so both steps read "12,00 €".
  const formatCurrency = (value: number) => formatPrice(value);

  const handlePriceChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPriceInput(event.target.value);
  };

  const handlePriceBlur = () => {
    const stored =
      typeof sellingPrice === 'number' ? sellingPrice.toFixed(2) : '';
    const trimmed = priceInput.trim();
    const parsed = Number(trimmed);
    // An emptied or unusable field shows the stored price again: clearing
    // does not unprice the product (patch semantics), so the card must not
    // suggest it does.
    if (
      !selection ||
      trimmed === '' ||
      !Number.isFinite(parsed) ||
      parsed <= 0
    ) {
      setPriceInput(stored);
      return;
    }
    const nextPrice = Math.round(parsed * 100) / 100;
    setPriceInput(nextPrice.toFixed(2));
    if (nextPrice === sellingPrice) return;
    // No clamp to the seller cost: the floor is shown as a hint and applied
    // by the server (selling_price_below_base_cost).
    setSellingPrice(product.id, nextPrice);
    useDesignStore.getState().setHasUnsavedChanges(true);
  };

  // UIProduct positions are the raw API rows (product-transformer casts
  // them), so they carry the default method and every offered one.
  const methodLabel = cardMethodLabel(
    product.positions as unknown as Parameters<typeof cardMethodLabel>[0],
    designDecoration
  );

  const renderColorSwatch = (color: UIProductColor) => {
    const isSelected = product.selectedColors.includes(color.id);
    const isColorFocused = isFocused && focusedColorId === color.id;
    const colorName = color.isHeathered
      ? t('creator.productCard.colorHeathered', { name: color.name })
      : color.name;
    return (
      <button
        key={color.id}
        onClick={() => {
          // An active product with exactly one color blocks deselecting it
          // (see productSelectionStore.toggleColor) - surface a toast so the
          // click doesn't look like it silently failed.
          if (
            product.isActive &&
            product.selectedColors.length === 1 &&
            isSelected
          ) {
            toast(t('creator.productCard.minOneColor'));
            return;
          }
          onColorToggle(product.id, color.id);
          if (isFocused) {
            onColorClick?.(product.id, color.id);
          }
        }}
        className={cn(
          'w-[15px] h-[15px] rounded-full shrink-0 transition-shadow',
          isColorFocused
            ? 'shadow-[inset_0_0_0_1px_rgba(13,13,13,0.18),0_0_0_2px_var(--paper-2),0_0_0_3.5px_rgb(var(--color-primary-500))]'
            : isSelected
              ? 'shadow-[inset_0_0_0_1px_rgba(13,13,13,0.18),0_0_0_2px_var(--paper-2),0_0_0_3.5px_var(--ink-2)]'
              : 'shadow-[inset_0_0_0_1px_rgba(13,13,13,0.18)] hover:shadow-[inset_0_0_0_1px_rgba(13,13,13,0.18),0_0_0_2px_var(--paper-2),0_0_0_3px_var(--ink-3)]'
        )}
        style={{ backgroundColor: color.hex }}
        title={colorName}
        aria-label={
          isSelected
            ? t('creator.productCard.deselectColor', { name: colorName })
            : t('creator.productCard.selectColor', { name: colorName })
        }
        aria-pressed={isSelected}
      />
    );
  };

  return (
    <div className="relative">
      <div
        className={cn(
          'dc-tile bg-[var(--paper-2)] rounded-card overflow-hidden transition-all cursor-pointer',
          mismatchLabel && 'opacity-60 hover:opacity-100',
          isFocused
            ? 'ring-1 ring-primary border border-transparent'
            : product.isActive
              ? 'border border-primary'
              : 'border border-[var(--line)] hover:border-[var(--ink-3)]'
        )}
        data-focused={isFocused || undefined}
        onClick={handleCardClick}
        onMouseEnter={handleMouseEnter}
      >
        {/* Header: Product Name + Placement pencil + Toggle */}
        <div className="flex items-center justify-between gap-2 px-3 py-2.5">
          <span className="dc-type-card-title text-[var(--ink)] truncate">
            {product.name}
          </span>
          <div
            className="flex items-center gap-1.5 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {showEditPlacement && (
              <button
                type="button"
                onClick={handleEditPlacement}
                title={t('creator.productCard.editPlacement')}
                aria-label={
                  hasOverrides
                    ? t('creator.productCard.editPlacementAdjusted')
                    : t('creator.productCard.editPlacement')
                }
                className={cn(
                  'relative flex h-8 w-8 items-center justify-center rounded-control ring-1 ring-inset transition-colors',
                  hasOverrides
                    ? 'bg-primary-50 text-primary ring-primary-200 hover:bg-primary-100'
                    : 'bg-[var(--paper)] text-[var(--mute)] ring-[var(--line)] hover:bg-[var(--paper-3)] hover:text-[var(--ink)]'
                )}
              >
                <Pencil className="h-3.5 w-3.5" />
                {hasOverrides && (
                  <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary ring-2 ring-[var(--paper-2)]" />
                )}
              </button>
            )}
            <Switch
              checked={product.isActive}
              onCheckedChange={(checked) => onToggle(product.id, checked)}
              className="h-5 w-[34px] data-[state=checked]:bg-primary data-[state=unchecked]:bg-[var(--paper-3)] data-[state=unchecked]:ring-1 data-[state=unchecked]:ring-inset data-[state=unchecked]:ring-[var(--line)]"
              thumbClassName="h-4 w-4 shadow-[0_0_0_1px_rgba(13,13,13,0.08)] data-[state=checked]:translate-x-[14px]"
            />
          </div>
        </div>
        {/* Main Product Image with secondary position Thumbnail inside */}
        {/* PERFORMANCE: Both templates are always rendered with STABLE references.
          Only their size/position changes based on activePosition.
          This prevents image reload on position switch. */}
        <div
          className={cn(
            'relative bg-[var(--paper-2)] overflow-hidden',
            mockupContainerClassName ?? 'aspect-square'
          )}
        >
          {/* Front Mockup - always rendered, size changes based on active position */}
          <div
            className={cn(
              'transition-all duration-150',
              isBackActive
                ? 'absolute bottom-2 left-2 z-10' // Thumbnail position
                : 'w-full h-full' // Full size
            )}
          >
            {isBackActive ? (
              // Thumbnail mode
              <div className="w-12 h-14 bg-[var(--paper)] rounded-control ring-1 ring-inset ring-[var(--line)] overflow-hidden p-0.5">
                <ProductMockupWithTemplate
                  product={product}
                  template={frontTemplate || null}
                  backgroundColor={displayColor}
                  isHeathered={isHeathered}
                  size="small"
                  scale={mockupScale}
                  designDataUrl={frontDesignUrl}
                  adaptedPlacement={frontAdaptation?.placement}
                  designCropUrl={frontAdaptation?.cropUrl}
                />
              </div>
            ) : (
              // Full size mode
              <ProductMockupWithTemplate
                product={product}
                template={frontTemplate || null}
                backgroundColor={displayColor}
                isHeathered={isHeathered}
                size="large"
                scale={mockupScale}
                designDataUrl={frontDesignUrl}
                adaptedPlacement={frontAdaptation?.placement}
                designCropUrl={frontAdaptation?.cropUrl}
              />
            )}
            {isBackActive && (
              <span className="dc-type-caption text-[var(--mute)] block text-center mt-0.5">
                {t('creator.positions.front')}
              </span>
            )}
          </div>

          {/* Back Mockup - always rendered if available, size changes based on active position */}
          {backTemplate && (
            <div
              className={cn(
                'transition-all duration-150',
                !isBackActive
                  ? 'absolute bottom-2 left-2 z-10' // Thumbnail position
                  : 'w-full h-full absolute inset-0' // Full size (behind front when front is thumbnail)
              )}
            >
              {!isBackActive ? (
                // Thumbnail mode
                <div className="w-12 h-14 bg-[var(--paper)] rounded-control ring-1 ring-inset ring-[var(--line)] overflow-hidden p-0.5">
                  <ProductMockupWithTemplate
                    product={product}
                    template={backTemplate}
                    backgroundColor={displayColor}
                    isHeathered={isHeathered}
                    size="small"
                    scale={mockupScale}
                    designDataUrl={backDesignUrl}
                    adaptedPlacement={backAdaptation?.placement}
                    designCropUrl={backAdaptation?.cropUrl}
                  />
                </div>
              ) : (
                // Full size mode
                <ProductMockupWithTemplate
                  product={product}
                  template={backTemplate}
                  backgroundColor={displayColor}
                  isHeathered={isHeathered}
                  size="large"
                  scale={mockupScale}
                  designDataUrl={backDesignUrl}
                  adaptedPlacement={backAdaptation?.placement}
                  designCropUrl={backAdaptation?.cropUrl}
                />
              )}
              {!isBackActive && (
                <span className="dc-type-caption text-[var(--mute)] block text-center mt-0.5">
                  {t('creator.positions.back')}
                </span>
              )}
            </div>
          )}

          {/* Fit: the reason a product does not carry the design, or the
              design's effective width per Position (plan KTD4). */}
          {(mismatchLabel ||
            dpiWarning ||
            (areaChips && areaChips.length > 0)) && (
            <div className="absolute bottom-2 right-2 flex max-w-[70%] flex-wrap justify-end gap-1">
              {mismatchLabel ? (
                <span className="dc-chip dc-type-chip rounded-chip bg-[var(--paper)] px-2 py-0.5 text-[var(--danger)] ring-1 ring-inset ring-[var(--line)]">
                  {mismatchLabel}
                </span>
              ) : (
                <>
                  {areaChips?.map((chip) => (
                    <span
                      key={chip}
                      className="dc-chip dc-type-chip rounded-chip bg-[var(--paper)] px-2 py-0.5 text-[var(--ink-2)] ring-1 ring-inset ring-[var(--line)]"
                    >
                      {chip}
                    </span>
                  ))}
                  {dpiWarning && (
                    <span
                      role="status"
                      className="dc-chip dc-type-chip rounded-chip bg-[var(--paper)] px-2 py-0.5 text-[var(--warning)] ring-1 ring-inset ring-[var(--line)]"
                    >
                      {dpiWarning}
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {/* Premium Badge (e.g., for organic products) */}
          {product.isPremium && (
            <div
              data-tone="success"
              className="dc-media-badge dc-type-chip absolute top-2 right-2 px-2 py-0.5 rounded-chip bg-[var(--paper)] ring-1 ring-inset ring-[var(--line)] text-[var(--success)]"
            >
              {t('creator.productCard.organic')}
            </div>
          )}

          {/* Heathered indicator */}
          {isHeathered && (
            <div className="dc-media-badge dc-type-chip absolute top-2 left-2 px-2 py-0.5 rounded-chip bg-[var(--paper)] ring-1 ring-inset ring-[var(--line)] text-[var(--ink-2)]">
              {t('creator.productCard.heathered')}
            </div>
          )}
        </div>
        {/* Color Selection - only show if product has colors */}
        {product.colors.length > 0 && (
          <div className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
            {isExpanded ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="dc-type-label flex items-center gap-1 text-[var(--mute)] shrink-0">
                    <span>{t('creator.productCard.colors')}</span>
                  </div>
                  {hasMoreColors && (
                    <button
                      onClick={() => setIsExpanded(false)}
                      className={cn(
                        'rounded-control ring-1 ring-inset ring-[var(--line)] bg-[var(--paper)] hover:bg-[var(--paper-3)] flex items-center justify-center transition shrink-0',
                        isCompactColors ? 'w-6 h-6' : 'w-5 h-5'
                      )}
                      aria-label={t('creator.productCard.collapseColors')}
                      aria-expanded={true}
                    >
                      <ChevronUp
                        className={cn(
                          'text-[var(--ink-2)]',
                          isCompactColors ? 'w-4 h-4' : 'w-3.5 h-3.5'
                        )}
                      />
                    </button>
                  )}
                </div>
                <div
                  className="grid w-full gap-1.5 justify-items-start"
                  style={{
                    gridTemplateColumns: `repeat(auto-fill, minmax(${SWATCH_SIZE}px, ${SWATCH_SIZE}px))`,
                  }}
                >
                  {visibleColors.map(renderColorSwatch)}
                </div>
              </div>
            ) : (
              <div ref={setColorRowEl} className="flex items-center gap-2">
                <div className="dc-type-label flex items-center gap-1 text-[var(--mute)] shrink-0">
                  <span>{t('creator.productCard.colors')}</span>
                </div>
                <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-visible flex-nowrap">
                  {visibleColors.map(renderColorSwatch)}
                </div>
                {hasMoreColors && (
                  <button
                    onClick={() => setIsExpanded(true)}
                    className={cn(
                      'rounded-control ring-1 ring-inset ring-[var(--line)] bg-[var(--paper)] hover:bg-[var(--paper-3)] flex items-center justify-center transition shrink-0 ml-1',
                      isCompactColors ? 'w-6 h-6' : 'w-5 h-5'
                    )}
                    aria-label={t('creator.productCard.expandColors')}
                    aria-expanded={false}
                  >
                    <ChevronUp
                      className={cn(
                        'text-[var(--ink-2)] transition-transform rotate-180',
                        isCompactColors ? 'w-4 h-4' : 'w-3.5 h-3.5'
                      )}
                    />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {/* No colors message for products without colors */}
        {product.colors.length === 0 && (
          <div className="px-3 py-2 text-xs text-[var(--mute)] text-center">
            {t('creator.productCard.noColors')}
          </div>
        )}
        {/* Pricing - hidden on Design step */}
        {currentStep !== 'design' ? (
          <div
            className="px-3 pb-3"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="bg-[var(--paper)] rounded-control p-3">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="min-w-0">
                  <label className="dc-type-label text-[var(--mute)] block mb-1">
                    {t('creator.productCard.sellerCost')}
                  </label>
                  <div className="dc-type-price text-[var(--ink)] whitespace-nowrap">
                    {quotePending ? (
                      <span
                        data-testid="price-skeleton"
                        aria-hidden="true"
                        className="inline-block h-4 w-14 animate-pulse rounded bg-[var(--paper-3)]"
                      />
                    ) : sellerCost === null ? (
                      '—'
                    ) : (
                      formatCurrency(sellerCost)
                    )}
                  </div>
                </div>
                <div className="min-w-0">
                  <label className="dc-type-label text-[var(--mute)] block mb-1">
                    {t('creator.productCard.sellingPrice')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--mute)] text-sm">
                      €
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      value={priceInput}
                      onChange={handlePriceChange}
                      onFocus={() => {
                        priceFieldFocused.current = true;
                      }}
                      onBlur={() => {
                        priceFieldFocused.current = false;
                        handlePriceBlur();
                      }}
                      disabled={!product.isActive}
                      className={cn(
                        'w-full pl-6 pr-2 py-1.5 text-sm bg-[var(--paper-2)] border-none rounded-control ring-1 ring-inset ring-[var(--line)] focus:ring-2 focus:ring-primary',
                        !product.isActive &&
                          'bg-[var(--paper-3)] text-[var(--mute)] cursor-not-allowed'
                      )}
                      placeholder="—"
                    />
                  </div>
                </div>
                <div className="min-w-0">
                  <label className="dc-type-label text-[var(--mute)] block mb-1">
                    {t('creator.productCard.profit')}
                  </label>
                  <div
                    className={cn(
                      'dc-type-price whitespace-nowrap',
                      profit === null || profit >= 0
                        ? 'text-[var(--success)]'
                        : 'text-[var(--danger)]'
                    )}
                  >
                    {quotePending ? (
                      <span
                        data-testid="price-skeleton"
                        aria-hidden="true"
                        className="inline-block h-4 w-14 animate-pulse rounded bg-[var(--paper-3)]"
                      />
                    ) : profit === null ? (
                      '—'
                    ) : (
                      formatCurrency(profit)
                    )}
                  </div>
                </div>
              </div>
              <div className="dc-type-note flex items-start justify-between gap-2 text-[var(--mute)]">
                <span className="min-w-0">
                  {quoteFailed ? (
                    <span className="text-[var(--danger)]">
                      {t('creator.productCard.quoteFailed')}{' '}
                      <button
                        type="button"
                        onClick={() => void pricingQuery.refetch()}
                        className="underline hover:text-[var(--ink)]"
                      >
                        {t('creator.productCard.retry')}
                      </button>
                    </span>
                  ) : quotePending ? null : belowFloor ? (
                    <span className="text-[var(--danger)]">
                      {t('creator.productCard.belowFloor', {
                        amount: formatCurrency(floorPrice ?? 0),
                      })}
                    </span>
                  ) : priceState === 'ok' ? null : (
                    t(`creator.productCard.${priceState}`)
                  )}
                </span>
                <span className="shrink-0">{methodLabel}</span>
              </div>
              {quote && !quotePending && quote.variants.length > 0 && (
                <VariantSurchargeList
                  variants={quote.variants}
                  sellingPrice={quote.sellingPrice}
                  selectedColors={product.selectedColors}
                  formatCurrency={formatCurrency}
                />
              )}
            </div>
          </div>
        ) : (
          <div
            className="px-2 pb-2"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="bg-[var(--paper)] rounded-control p-2">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div className="min-w-0">
                  <label className="dc-type-label text-[var(--mute)] block mb-1">
                    {t('creator.productCard.blankCost')}
                  </label>
                  <div className="dc-type-price text-[var(--ink)] whitespace-nowrap">
                    {product.blankCost === null
                      ? '—'
                      : formatCurrency(product.blankCost)}
                  </div>
                </div>
                <div className="min-w-0 text-right">
                  <label className="dc-type-label text-[var(--mute)] block mb-1">
                    {t('creator.productCard.method')}
                  </label>
                  <div className="dc-type-data text-[var(--ink)] whitespace-nowrap">
                    {methodLabel}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Inline popover: "Only this product" vs "All products" */}
      {showSwitchPopover && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            onDismissPopover?.();
          }}
        >
          <div className="absolute inset-0 bg-black/30 rounded-card" />
          <div
            className="relative bg-[var(--paper)] rounded-control ring-1 ring-inset ring-[var(--line)] p-3 mx-2 w-full max-w-[200px]"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-xs text-[var(--ink-2)] mb-2 text-center font-medium">
              {t('creator.productCard.applyDesignTo')}
            </p>
            <div className="flex flex-col gap-1.5">
              <button
                onClick={onSingleProduct}
                className="w-full px-3 py-1.5 text-xs font-medium rounded-control bg-primary-50 text-primary hover:bg-primary-100 ring-1 ring-inset ring-primary-200 transition-colors"
              >
                {t('creator.productCard.onlyThisProduct')}
              </button>
              <button
                onClick={onAllProducts}
                className="w-full px-3 py-1.5 text-xs font-medium rounded-control bg-[var(--paper-2)] text-[var(--ink)] hover:bg-[var(--paper-3)] ring-1 ring-inset ring-[var(--line)] transition-colors"
              >
                {t('creator.productCard.allProducts')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
